import { authedFetch } from "@/lib/authed-fetch";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ProjectWithRelations, type User } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { X, Clock, Lock, Archive, Download, AlertTriangle, Cloud, Loader2, FileText, Trash2, Tag } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { refreshAuthToken, clearAuth } from "@/lib/auth";
import { useDebugWindow } from "@/hooks/use-debug-render";
import { useAuth } from "@/contexts/auth-context";
import { useWebSocket } from "@/contexts/websocket-context";
import { BackupHistoryTimeline } from "@/components/backup-history-timeline";

// Archive progress state interface
interface ArchiveProgress {
  phase: string;
  current: number;
  total: number;
  percentage: number;
  detail?: string;
  failedFiles?: Array<{ path: string; error: string }>;
}

// Helper to get human-readable phase labels
function getPhaseLabel(phase: string): string {
  switch (phase) {
    case 'starting': return 'Starting backup...';
    case 'exporting_data': return 'Exporting database...';
    case 'discovering_files': return 'Scanning files...';
    case 'downloading_attachments': return 'Downloading attachments...';
    case 'finalizing': return 'Creating ZIP file...';
    case 'saving': return 'Saving to disk...';
    case 'complete': return 'Archive ready!';
    case 'partial_complete': return 'Archive ready (with warnings)';
    case 'error': return 'Error occurred';
    default: return 'Processing...';
  }
}

interface ProjectEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectWithRelations;
}

export function ProjectEditDialog({ open, onOpenChange, project }: ProjectEditDialogProps) {
  const [projectName, setProjectName] = useState<string>("");
  const [leadArtist, setLeadArtist] = useState<string>("");
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [timeTrackingEnabled, setTimeTrackingEnabled] = useState<boolean>(true);
  const [isFrozen, setIsFrozen] = useState<boolean>(false);
  const [showDropboxArchiveDialog, setShowDropboxArchiveDialog] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState<ArchiveProgress | null>(null);
  const [archiveReady, setArchiveReady] = useState<{
    archiveId: string;
    downloadUrl: string;
    filename: string;
    size: number;
    expiresAt: number; // Unix timestamp when the download link expires (1 hour TTL)
  } | null>(null);
  const [archiveTimeLeft, setArchiveTimeLeft] = useState<string>(''); // Human-readable time remaining
  
  // HTML backup progress state
  const [htmlBackupProgress, setHtmlBackupProgress] = useState<{stage: string, message: string, percentage: number | null} | null>(null);

  // Force-rebuild confirmation dialog state (task #216)
  const [showForceRebuildDialog, setShowForceRebuildDialog] = useState(false);

  // Archive warning dialog state
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [warningDetails, setWarningDetails] = useState<{
    failedFiles: Array<{ path: string; error: string }>;
    succeeded: number;
    failed: number;
  } | null>(null);
  
  const { toast } = useToast();
  const { subscribeToProgress, unsubscribeFromProgress } = useWebSocket();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isLeadArtist = !!(user?.shortCode && project.leadArtist === user.shortCode);
  const canAccessBackup = isAdmin || isLeadArtist;
  
  // Track project edit dialog window state
  useDebugWindow('ProjectEditDialog', open);

  // Fetch fresh project data when dialog opens to ensure we have the latest values
  const { data: freshProject } = useQuery<ProjectWithRelations>({
    queryKey: [`/api/projects/${project.id}`],
    enabled: open, // Only fetch when dialog is open
  });

  // Fetch artists from database
  const { data: artists = [] } = useQuery<User[]>({
    queryKey: ['/api/artists'],
  });

  // Initialize form values from fresh project data or fallback to passed project
  useEffect(() => {
    const projectData = freshProject || project;
    if (projectData && open) {
      setProjectName(projectData.projectName || "");
      setLeadArtist(projectData.leadArtist === "" ? "none" : projectData.leadArtist || "none");
      setSelectedArtists(projectData.artists?.map((a: any) => a.artistCode) || []);
      setTimeTrackingEnabled(projectData.timeTrackingEnabled ?? true);
      setIsFrozen(projectData.lifecycleState === 'deactivated');
    }
  }, [freshProject, project, open]);
  
  // Ref to track the completion timeout for cleanup
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track mount status for async operations
  const isMountedRef = useRef<boolean>(false);
  
  // Reset progress state when dialog opens to clear any stale state
  useEffect(() => {
    if (open) {
      setArchiveProgress(null);
      setIsDownloading(false);
      setArchiveReady(null);
      setArchiveTimeLeft('');
      setHtmlBackupProgress(null);
    }
  }, [open]);
  
  // Timer to update archive time remaining and check for expiration
  useEffect(() => {
    if (!archiveReady) {
      setArchiveTimeLeft('');
      return;
    }
    
    const updateTimeLeft = () => {
      const now = Date.now();
      const remaining = archiveReady.expiresAt - now;
      
      if (remaining <= 0) {
        // Archive has expired
        setArchiveReady(null);
        setArchiveTimeLeft('');
        return;
      }
      
      // Format remaining time
      const minutes = Math.floor(remaining / 60000);
      if (minutes >= 60) {
        setArchiveTimeLeft(`${Math.floor(minutes / 60)}h ${minutes % 60}m left`);
      } else if (minutes > 0) {
        setArchiveTimeLeft(`${minutes}m left`);
      } else {
        const seconds = Math.floor(remaining / 1000);
        setArchiveTimeLeft(`${seconds}s left`);
      }
    };
    
    // Update immediately
    updateTimeLeft();
    
    // Update every 30 seconds (or every second if less than a minute left)
    const interval = setInterval(updateTimeLeft, 30000);
    
    return () => clearInterval(interval);
  }, [archiveReady]);
  
  // Archive progress WebSocket listener - stays active while dialog is open
  useEffect(() => {
    // Require both functions to be available before registering
    if (!subscribeToProgress || !unsubscribeFromProgress) return;
    // Only subscribe when dialog is open
    if (!open) return;
    
    // Mark as mounted
    isMountedRef.current = true;
    
    // Unique subscription ID for this dialog instance
    const subscriptionId = `archive-progress-${project.id}`;
    
    const handleProgress = (data: any) => {
      // Guard against updates after unmount
      if (!isMountedRef.current) return;
      
      // Clear any previous completion timeout when new progress arrives
      // This prevents stale timeouts from previous runs wiping current progress
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
      
      console.log('[Archive] Progress received:', data.phase, data.percentage + '%');
      setArchiveProgress({
        phase: data.phase,
        current: data.current,
        total: data.total,
        percentage: data.percentage,
        detail: data.detail,
        failedFiles: data.failedFiles,
      });
      
      // Show warning dialog when partial_complete with failed files
      if (data.phase === 'partial_complete' && data.failedFiles && data.failedFiles.length > 0) {
        setWarningDetails({
          failedFiles: data.failedFiles,
          succeeded: data.succeeded || 0,
          failed: data.failed || data.failedFiles.length,
        });
        setShowWarningDialog(true);
      }
      
      // Clear progress on terminal phases after a delay
      const isTerminalPhase = data.phase === 'complete' || data.phase === 'partial_complete' || data.phase === 'error';
      if (isTerminalPhase) {
        completionTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setArchiveProgress(null);
            setIsDownloading(false);
          }
        }, data.phase === 'error' ? 5000 : 3000); // Longer delay for errors
      }
    };
    
    // Subscribe with filter for this specific project
    subscribeToProgress(subscriptionId, handleProgress, {
      type: 'archive.progress',
      projectId: project.id,
    });
    
    // Cleanup when dependencies change or dialog closes
    return () => {
      // 1. First mark unmounted to prevent async updates
      isMountedRef.current = false;
      
      // 2. Cancel any pending timeouts
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
      
      // 3. Unsubscribe from WebSocket
      unsubscribeFromProgress(subscriptionId);
      
      // Note: State is NOT reset here to avoid React warnings about 
      // setting state on unmounted components. The state will be reset
      // when the effect runs again on next open (isMountedRef.current = true).
      // The UI conditionally renders based on state, so stale state is harmless.
    };
  }, [open, project.id, subscribeToProgress, unsubscribeFromProgress]);
  
  // Ref for HTML backup completion timeout
  const htmlBackupCompletionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // HTML backup progress WebSocket listener
  useEffect(() => {
    if (!subscribeToProgress || !unsubscribeFromProgress) return;
    if (!open) return;
    
    isMountedRef.current = true;
    
    const subscriptionId = `html-backup-progress-${project.id}`;
    
    const handleHtmlBackupProgress = (message: any) => {
      if (!isMountedRef.current) return;
      
      // Filter by userId to prevent other users' backup events from triggering toasts
      if (message.userId && message.userId !== user?.id) return;
      
      // Clear any previous completion timeout
      if (htmlBackupCompletionTimeoutRef.current) {
        clearTimeout(htmlBackupCompletionTimeoutRef.current);
        htmlBackupCompletionTimeoutRef.current = null;
      }
      
      console.log('[HTML Backup] Progress received:', message.stage, message.message, message.percentage);
      
      setHtmlBackupProgress({
        stage: message.stage,
        message: message.message,
        percentage: message.percentage ?? null,
      });
      
      // Handle terminal states
      if (message.stage === 'complete') {
        toast({
          title: 'HTML Backup Created',
          description: message.message,
        });
        
        htmlBackupCompletionTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setHtmlBackupProgress(null);
          }
        }, 2000);
      } else if (message.stage === 'error') {
        toast({
          variant: 'destructive',
          title: 'HTML Backup Failed',
          description: message.message,
        });
        
        htmlBackupCompletionTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setHtmlBackupProgress(null);
          }
        }, 3000);
      }
    };
    
    subscribeToProgress(subscriptionId, handleHtmlBackupProgress, {
      type: 'html-backup.progress',
      projectId: project.id,
    });
    
    return () => {
      isMountedRef.current = false;
      
      if (htmlBackupCompletionTimeoutRef.current) {
        clearTimeout(htmlBackupCompletionTimeoutRef.current);
        htmlBackupCompletionTimeoutRef.current = null;
      }
      unsubscribeFromProgress(subscriptionId);
    };
  }, [open, project.id, subscribeToProgress, unsubscribeFromProgress, toast]);
  
  // Lifecycle toggle mutation (admin only)
  const lifecycleMutation = useMutation({
    mutationFn: async (newState: 'active' | 'deactivated') => {
      const response = await apiRequest('PATCH', `/api/projects/${project.id}/lifecycle`, { state: newState });
      return response.json();
    },
    onSuccess: (data) => {
      // Close dialog immediately to prevent "Save Changes" from being clicked
      onOpenChange(false);
      
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}`] });
      
      toast({
        title: data.lifecycleState === 'active' ? 'Project Reactivated' : 'Project Frozen',
        description: data.lifecycleState === 'active' 
          ? 'Project is now editable again.' 
          : 'Project is now frozen. No changes can be made.',
      });
    },
    onError: (error: any) => {
      // Revert UI state on error
      setIsFrozen(project.lifecycleState === 'deactivated');
      toast({
        variant: 'destructive',
        title: 'Failed to update project state',
        description: error.message || 'Please try again.',
      });
    },
  });

  const handleFreezeToggle = (checked: boolean) => {
    setIsFrozen(checked);
    lifecycleMutation.mutate(checked ? 'deactivated' : 'active');
  };

  // Pulse count for the delete-project precondition. Admin-only feature —
  // skip the query for non-admins so we don't waste a request. Uses the
  // existing paginated pulses endpoint with limit=1 just to read `total`.
  const { data: pulseCountData, isLoading: pulseCountLoading } = useQuery<{
    data: any[];
    total: number;
    hasMore: boolean;
  }>({
    queryKey: [`/api/projects/${project.id}/pulses`, { paginate: 'true', limit: 1, loadTasks: 'false' }],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${project.id}/pulses?paginate=true&limit=1&loadTasks=false`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error('Failed to load pulse count');
      return res.json();
    },
    enabled: open && isAdmin,
  });
  const pulseCount = pulseCountData?.total ?? null;
  const canDeleteProject = isAdmin && pulseCount === 0;

  // Hard-delete project mutation (admin only, only when 0 pulses).
  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('DELETE', `/api/projects/${project.id}`);
      return response.json();
    },
    onSuccess: () => {
      setShowDeleteDialog(false);
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      toast({
        title: 'Project Deleted',
        description: `Project ${project.id} has been permanently removed.`,
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Failed to delete project',
        description: error.message || 'Please try again.',
      });
    },
  });

  // Backup to Dropbox mutation (admin only) - NON-DESTRUCTIVE snapshot backup
  const backupToDropboxMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', `/api/projects/${project.id}/backup-to-dropbox`);
      return response.json();
    },
    onSuccess: (data) => {
      setShowDropboxArchiveDialog(false);
      
      toast({
        title: 'Project Backup Created',
        description: `Snapshot saved to Dropbox: ${data.backupPath}`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/backup-history?days=60`] });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Backup Failed',
        description: error.message || 'Failed to backup project to Dropbox.',
      });
    },
  });
  
  // HTML Backup mutation - generates a portable HTML overview file
  const htmlBackupMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', `/api/projects/${project.id}/html-backup`);
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'HTML Backup Created',
        description: `Overview saved to Dropbox: ${data.path}`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/backup-history?days=60`] });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/html-backup/cache-status`] });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'HTML Backup Failed',
        description: error.message || 'Failed to generate HTML backup.',
      });
    },
  });

  // Per-project HTML backup cache status (admin only, task #216)
  type CacheTier = 'fresh' | 'pending' | 'overdue' | 'failed';
  const { data: cacheStatus } = useQuery<{
    tier: CacheTier;
    reason: 'last_backup_failed' | 'queue_stuck' | 'overdue_dirty' | 'never_backed_up' | null;
    htmlDirty: boolean;
    lastActivityAt: string | null;
    lastHtmlBackupAt: string | null;
    lastSuccessfulBackupAt: string | null;
    activityCount: number;
    lastErrorMessage: string | null;
    hasStuckQueue: boolean;
    coverage: { cached: number; total: number };
  }>({
    queryKey: [`/api/projects/${project.id}/html-backup/cache-status`],
    enabled: open && isAdmin,
    refetchInterval: 30_000,
  });

  // Refresh cache-status when the server broadcasts a backup status change or
  // any html-backup.progress completes (#216 review).
  useEffect(() => {
    if (!subscribeToProgress || !unsubscribeFromProgress || !open || !isAdmin) return;
    const subId = `cache-status-refresh-${project.id}`;
    subscribeToProgress(subId, (msg: any) => {
      if (msg.projectId && msg.projectId !== project.id) return;
      if (msg.type === 'backup:status_changed' || msg.stage === 'complete' || msg.stage === 'error') {
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/html-backup/cache-status`] });
      }
    });
    return () => unsubscribeFromProgress(subId);
  }, [subscribeToProgress, unsubscribeFromProgress, open, isAdmin, project.id, queryClient]);

  // Force full rebuild — wipes cache and re-renders every pulse fragment.
  const forceRebuildMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST',
        `/api/projects/${project.id}/html-backup?invalidateCache=1`,
      );
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Full rebuild started',
        description: 'Cache wiped — backup is rebuilding every pulse fragment.',
      });
      setShowForceRebuildDialog(false);
      // Refresh both the per-project cache-status badge AND the admin queue
      // list (#216 review): the rebuild enqueues a new backup job.
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/html-backup/cache-status`] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/history'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/html-backup/cache-health'] });
    },
    onError: (error: any) => {
      // 504/500/abort → tell the user to retry, don't rethrow.
      toast({
        variant: 'destructive',
        title: 'Rebuild request timed out',
        description: 'Check Admin Notifications for progress and run again.',
      });
      setShowForceRebuildDialog(false);
    },
  });

  // Create archive handler - starts the archive creation process
  const handleCreateArchive = async () => {
    setIsDownloading(true);
    setArchiveReady(null);
    try {
      // Make POST request to create archive
      let response = await authedFetch(`/api/projects/${project.id}/archive/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      // If 401, try to refresh token once
      if (response.status === 401) {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          response = await authedFetch(`/api/projects/${project.id}/archive/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
        } else {
          clearAuth();
          window.location.href = '/login';
          return;
        }
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Archive creation failed');
      }
      
      const data = await response.json();
      
      // Store the download info with 1-hour expiration
      const archiveInfo = {
        archiveId: data.archiveId,
        downloadUrl: data.downloadUrl,
        filename: data.filename,
        size: data.size,
        expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour from now
      };
      setArchiveReady(archiveInfo);
      
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}/backup-history?days=60`] });
      
      // Auto-trigger download immediately using the shared download function
      // This handles auth refresh and shows proper error toasts if it fails
      await performArchiveDownload(archiveInfo, true);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Archive Creation Failed',
        description: error.message || 'Failed to create project archive.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Core download function - handles auth refresh and can be called for auto or manual download
  const performArchiveDownload = async (
    archiveInfo: { archiveId: string; filename: string; expiresAt: number },
    isAutoDownload: boolean = false
  ): Promise<boolean> => {
    // Check if archive has expired
    if (Date.now() > archiveInfo.expiresAt) {
      if (!isAutoDownload) {
        toast({
          variant: 'destructive',
          title: 'Archive Expired',
          description: 'This archive has expired. Please create a new one.',
        });
      }
      setArchiveReady(null);
      return false;
    }
    
    try {
      // Step 1: Request a short-lived download token (authenticated)
      let tokenResponse = await authedFetch(`/api/archives/${archiveInfo.archiveId}/download-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      // If 401, try to refresh token once
      if (tokenResponse.status === 401) {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          tokenResponse = await authedFetch(`/api/archives/${archiveInfo.archiveId}/download-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
        } else {
          if (!isAutoDownload) {
            clearAuth();
            window.location.href = '/login';
          }
          return false;
        }
      }
      
      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        // If server says expired, clear the state
        if (errorData.error?.includes('expired') || errorData.error?.includes('not found')) {
          setArchiveReady(null);
        }
        throw new Error(errorData.error || 'Failed to get download token');
      }
      
      const { downloadUrl } = await tokenResponse.json();
      
      // Step 2: Use direct browser navigation with the download token
      window.location.href = downloadUrl;
      
      toast({
        title: 'Download Started',
        description: isAutoDownload 
          ? `Archive ${archiveInfo.filename} is downloading automatically. You can re-download within 1 hour.`
          : `Archive file ${archiveInfo.filename} is downloading. You can re-download within the time limit.`,
      });
      
      return true;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to download archive. It may have expired.';
      
      // Always show toast for failures (even auto-download) so user knows to retry manually
      toast({
        variant: 'destructive',
        title: isAutoDownload ? 'Auto-Download Failed' : 'Download Failed',
        description: isAutoDownload 
          ? `${errorMessage} Click the Download button to try again.`
          : errorMessage,
      });
      
      // Only clear if it's an expiration error
      if (error.message?.includes('expired') || error.message?.includes('not found')) {
        setArchiveReady(null);
      }
      return false;
    }
  };

  // Download the prepared archive file using direct browser navigation
  const handleDownloadArchive = async () => {
    if (!archiveReady) return;
    await performArchiveDownload(archiveReady, false);
  };
  
  const updateProjectMutation = useMutation({
    mutationFn: async (updateData: any) => {
      const response = await apiRequest('PATCH', `/api/projects/${project.id}`, updateData);
      return response.json();
    },
    onSuccess: async (data) => {
      // Close dialog first to trigger re-render
      onOpenChange(false);
      
      // CRITICAL FIX: Use simple, direct invalidations like sequence operations do
      // Complex predicate-based invalidations can cause timing issues
      
      // Invalidate main project caches
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${project.id}`] });
      
      // CRITICAL: Invalidate years cache - this is what displays artist data in main table
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      
      // Also invalidate optimized years cache
      queryClient.invalidateQueries({ queryKey: ['/api/years', 'optimized'] });
      
      // Invalidate paginated projects cache 
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === '/api/projects' && key[1] === 'paginated';
        }
      });
      
      // Invalidate batch queries that contain time tracking info
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && (
            key[0] === '/api/pulses/batch-metadata' ||
            key[0] === '/api/pulses/batch/summary' ||
            (typeof key[0] === 'string' && 
             key[0].startsWith('/api/projects/') && 
             key[0].endsWith('/pulse-progress'))
          );
        }
      });
      
      toast({
        title: "Success",
        description: "Project updated successfully"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive"
      });
    }
  });

  const handleSave = async () => {
    // If project is frozen, don't allow saving changes - just close the dialog
    if (isFrozen) {
      toast({
        title: "Project Frozen",
        description: "Cannot save changes to a frozen project. Unfreeze it first.",
        variant: "destructive"
      });
      return;
    }
    
    if (!projectName.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive"
      });
      return;
    }

    const updateData = {
      projectName: projectName.trim(),
      leadArtist: leadArtist === "none" ? "" : leadArtist,
      artists: selectedArtists,
      timeTrackingEnabled: timeTrackingEnabled
    };
    
    updateProjectMutation.mutate(updateData);
  };

  const handleAddArtist = (artistId: string) => {
    if (!selectedArtists.includes(artistId)) {
      setSelectedArtists([...selectedArtists, artistId]);
    }
  };

  const handleRemoveArtist = (artistId: string) => {
    setSelectedArtists(selectedArtists.filter(id => id !== artistId));
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="navy-card border-gray-600 max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-white">
            Edit Project: {project.id}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Update project name and team assignments
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pr-2">
          {/* Project Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Project Details</h3>
            <div>
              <Label className="text-gray-300">Project Name *</Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                className="navy-bg border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Time Tracking Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Time Tracking</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" />
                <div>
                  <Label className="text-white">Enable Time Tracking</Label>
                  <p className="text-sm text-gray-400">
                    Allow time tracking for this project
                  </p>
                </div>
              </div>
              <Switch
                checked={timeTrackingEnabled}
                onCheckedChange={setTimeTrackingEnabled}
                className="data-[state=checked]:bg-blue-600"
              />
            </div>
            {!timeTrackingEnabled && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/50 rounded">
                <p className="text-sm text-yellow-200">
                  When disabled, the Time button and tracked times will be hidden for all pulses in this project.
                </p>
              </div>
            )}
          </div>

          {/* Backup History Section (Visible to all users) */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Backup Status</h3>
            <BackupHistoryTimeline projectId={project.id} />
          </div>

          {/* Search Keywords Section — read-only view of LLM-generated keywords (task #245) */}
          <ProjectSearchKeywordsSection projectId={project.id} open={open} isAdmin={isAdmin} />


          {/* Project Freeze Section (Admin Only) */}
          {isAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Project Status</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-yellow-500" />
                  <div>
                    <Label className="text-white">Freeze Project</Label>
                    <p className="text-sm text-gray-400">
                      When frozen, no changes can be made to this project
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isFrozen}
                  onCheckedChange={handleFreezeToggle}
                  disabled={lifecycleMutation.isPending}
                  className="data-[state=checked]:bg-yellow-600"
                  data-testid={`freeze-toggle-${project.id}`}
                />
              </div>
              {isFrozen && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/50 rounded">
                  <p className="text-sm text-yellow-200">
                    This project is frozen. All editing, status changes, and new content creation are blocked.
                  </p>
                </div>
              )}

              {/* Danger Zone — hard-delete only when no pulses exist yet. */}
              <div className="pt-2 mt-2 border-t border-red-900/40">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <Label className="text-white">Delete Project</Label>
                      <p className="text-sm text-gray-400">
                        {pulseCountLoading
                          ? 'Checking project contents…'
                          : canDeleteProject
                            ? 'Permanently removes the project. Available because no pulses exist yet.'
                            : pulseCount === null
                              ? 'Loading…'
                              : `Disabled: project has ${pulseCount} pulse${pulseCount === 1 ? '' : 's'}. Delete is only allowed for empty projects.`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={!canDeleteProject || deleteProjectMutation.isPending || pulseCountLoading}
                    className="border-red-700 text-red-300 hover:bg-red-900/30 hover:text-red-200 disabled:opacity-40"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Project Backup Section (Admin + Lead Artist - Available for any project state) */}
          {canAccessBackup && project.lifecycleState !== 'archived' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-blue-800 pb-2 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-blue-400" />
                Backup Project
              </h3>
              {isAdmin && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/50 rounded">
                  <div className="flex items-start gap-2">
                    <Archive className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-200 font-medium">Forensic Backup</p>
                      <p className="text-sm text-blue-300/80 mt-1">
                        Creates a complete backup of all project data and assets to Dropbox. 
                        Data remains in the database until manually purged.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!isAdmin && isLeadArtist && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/50 rounded">
                  <div className="flex items-start gap-2">
                    <Download className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-200 font-medium">Download Project Archive</p>
                      <p className="text-sm text-blue-300/80 mt-1">
                        Download a complete ZIP archive of all project files and attachments.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className={`grid grid-cols-1 ${isAdmin ? 'sm:grid-cols-3' : ''} gap-3`}>
                {isAdmin && (
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-300 hover:bg-blue-900/30 hover:text-blue-200"
                    onClick={() => setShowDropboxArchiveDialog(true)}
                    disabled={backupToDropboxMutation.isPending}
                    data-testid={`archive-dropbox-${project.id}`}
                  >
                    <Cloud className="w-4 h-4 mr-2" />
                    {backupToDropboxMutation.isPending ? 'Backing up...' : 'Backup to Dropbox'}
                  </Button>
                )}
                
                {isAdmin && (
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/30 hover:text-emerald-200"
                      onClick={() => htmlBackupMutation.mutate()}
                      disabled={htmlBackupMutation.isPending || !!htmlBackupProgress}
                      data-testid={`html-backup-${project.id}`}
                    >
                      {htmlBackupMutation.isPending || htmlBackupProgress ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {htmlBackupProgress?.percentage != null ? `${htmlBackupProgress.percentage}%` : 'Starting...'}
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          HTML Backup
                        </>
                      )}
                    </Button>
                    {isAdmin && cacheStatus && (
                      <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`cache-status-${project.id}`}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={
                                  cacheStatus.tier === 'fresh'
                                    ? 'border-green-600 text-green-300 cursor-help'
                                    : cacheStatus.tier === 'pending'
                                    ? 'border-slate-500 text-slate-300 cursor-help'
                                    : cacheStatus.tier === 'overdue'
                                    ? 'border-orange-600 text-orange-300 cursor-help'
                                    : 'border-red-600 text-red-300 cursor-help'
                                }
                                data-testid={`cache-tier-${cacheStatus.tier}`}
                              >
                                {cacheStatus.tier === 'fresh' && 'Cache fresh'}
                                {cacheStatus.tier === 'pending' && "Pending tonight's backup"}
                                {cacheStatus.tier === 'overdue' && 'Overdue'}
                                {cacheStatus.tier === 'failed' && (cacheStatus.hasStuckQueue ? 'Backup stuck' : 'Last backup failed')}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs text-xs space-y-1">
                              <div>
                                <span className="text-gray-400">Last activity:</span>{' '}
                                {cacheStatus.lastActivityAt ? new Date(cacheStatus.lastActivityAt).toLocaleString() : '—'}
                              </div>
                              <div>
                                <span className="text-gray-400">Last HTML backup:</span>{' '}
                                {cacheStatus.lastHtmlBackupAt ? new Date(cacheStatus.lastHtmlBackupAt).toLocaleString() : '—'}
                              </div>
                              <div>
                                <span className="text-gray-400">Last successful:</span>{' '}
                                {cacheStatus.lastSuccessfulBackupAt ? new Date(cacheStatus.lastSuccessfulBackupAt).toLocaleString() : '—'}
                              </div>
                              <div>
                                <span className="text-gray-400">Coverage:</span>{' '}
                                {cacheStatus.coverage.cached}/{cacheStatus.coverage.total}
                                {cacheStatus.coverage.total > 0 &&
                                  ` (${Math.round((cacheStatus.coverage.cached / cacheStatus.coverage.total) * 100)}%)`}
                              </div>
                              {cacheStatus.tier === 'failed' && cacheStatus.lastErrorMessage && (
                                <div className="pt-1 border-t border-red-900/50 text-red-300">
                                  <span className="text-gray-400">Error:</span> {cacheStatus.lastErrorMessage}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-gray-400 cursor-help">
                                {cacheStatus.coverage.cached}/{cacheStatus.coverage.total} im Cache
                                {cacheStatus.coverage.total > 0 && (
                                  <> · {Math.round((cacheStatus.coverage.cached / cacheStatus.coverage.total) * 100)}%</>
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs">
                              The HTML backup file contains all pulses — this number only shows how many are already cached.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-red-300 hover:text-red-200 hover:bg-red-900/30"
                          onClick={() => setShowForceRebuildDialog(true)}
                          disabled={forceRebuildMutation.isPending}
                          data-testid={`force-rebuild-${project.id}`}
                        >
                          Force full rebuild
                        </Button>
                      </div>
                    )}
                    {htmlBackupProgress && (
                      <div className="space-y-1">
                        <Progress value={htmlBackupProgress.percentage ?? 0} className="h-1.5" />
                        <p className="text-xs text-emerald-300/80 text-center truncate">{htmlBackupProgress.message}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {archiveReady ? (
                  <Button
                    variant="outline"
                    className="border-green-600 text-green-300 hover:bg-green-900/30 hover:text-green-200"
                    onClick={handleDownloadArchive}
                    data-testid={`download-archive-${project.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download ZIP ({(archiveReady.size / (1024 * 1024)).toFixed(1)} MB) - {archiveTimeLeft}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/30 hover:text-emerald-200"
                    onClick={handleCreateArchive}
                    disabled={isDownloading}
                    data-testid={`create-archive-${project.id}`}
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Archive className="w-4 h-4 mr-2" />
                        Create ZIP Archive
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Archive Progress Indicator */}
              {(isDownloading || archiveProgress) && !archiveReady && (
                <div className={`p-3 border rounded space-y-2 ${
                  archiveProgress?.phase === 'error' 
                    ? 'bg-red-900/30 border-red-700' 
                    : archiveProgress?.phase === 'partial_complete'
                    ? 'bg-yellow-900/30 border-yellow-700'
                    : archiveProgress?.phase === 'complete'
                    ? 'bg-green-900/30 border-green-700'
                    : 'bg-gray-800/50 border-gray-700'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm flex items-center gap-2 ${
                      archiveProgress?.phase === 'error' ? 'text-red-300' :
                      archiveProgress?.phase === 'partial_complete' ? 'text-yellow-300' :
                      archiveProgress?.phase === 'complete' ? 'text-green-300' :
                      'text-gray-300'
                    }`}>
                      {archiveProgress?.phase === 'complete' || archiveProgress?.phase === 'partial_complete' ? (
                        <Cloud className="w-4 h-4" />
                      ) : archiveProgress?.phase === 'error' ? (
                        <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      )}
                      {archiveProgress ? getPhaseLabel(archiveProgress.phase) : 'Preparing...'}
                    </span>
                    <span className="text-sm text-gray-400">
                      {archiveProgress?.percentage ?? 0}%
                    </span>
                  </div>
                  <Progress 
                    value={archiveProgress?.percentage ?? 0} 
                    className="h-2"
                  />
                  {archiveProgress?.detail && (
                    <p className={`text-xs truncate ${
                      archiveProgress.phase === 'error' ? 'text-red-400' :
                      archiveProgress.phase === 'partial_complete' ? 'text-yellow-400' :
                      'text-gray-500'
                    }`}>
                      {archiveProgress.detail}
                    </p>
                  )}
                  {archiveProgress?.phase === 'downloading_attachments' && archiveProgress.total && archiveProgress.total > 0 && (
                    <p className="text-xs text-gray-400">
                      {archiveProgress.current} / {archiveProgress.total} files
                    </p>
                  )}
                </div>
              )}
              
              {/* Archive Ready Indicator */}
              {archiveReady && (
                <div className="p-3 bg-green-900/30 border border-green-700 rounded">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-300">
                      <Cloud className="w-4 h-4" />
                      <span className="text-sm font-medium">Archive Ready!</span>
                    </div>
                    <span className="text-xs text-green-400 font-medium">{archiveTimeLeft}</span>
                  </div>
                  <p className="text-xs text-green-400 mt-1">
                    Click the download button to re-download. Download started automatically.
                  </p>
                </div>
              )}
              
              {isAdmin && (
                <p className="text-xs text-gray-500">
                  Generates a forensic backup. Data remains in the database until manually purged.
                </p>
              )}
            </div>
          )}

          {/* Team Assignment Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Team Assignment</h3>
            
            <div>
              <Label className="text-gray-300">Lead Artist</Label>
              <Select value={leadArtist} onValueChange={setLeadArtist}>
                <SelectTrigger className="navy-bg border-gray-600 text-white">
                  <SelectValue placeholder="Select lead artist (optional)" />
                </SelectTrigger>
                <SelectContent className="navy-card border-gray-600">
                  <SelectItem value="none" className="text-white">No lead artist</SelectItem>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.shortCode || ''} className="text-white">
                      {artist.shortCode} - {artist.firstName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-300">Additional Artists</Label>
              <Select onValueChange={handleAddArtist}>
                <SelectTrigger className="navy-bg border-gray-600 text-white">
                  <SelectValue placeholder="Add artist to team" />
                </SelectTrigger>
                <SelectContent className="navy-card border-gray-600">
                  {artists
                    .filter(artist => !selectedArtists.includes(artist.shortCode || '') && (leadArtist === "none" || artist.shortCode !== leadArtist))
                    .map((artist) => (
                      <SelectItem key={artist.id} value={artist.shortCode || ''} className="text-white">
                        {artist.shortCode} - {artist.firstName} {artist.lastName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              
              {selectedArtists.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedArtists.map((artistId) => {
                    const artist = artists.find(a => a.shortCode === artistId);
                    return artist ? (
                      <Badge key={artistId} variant="secondary" className="bg-blue-900 text-white">
                        {artist.shortCode} - {artist.firstName} {artist.lastName}
                        <button
                          onClick={() => handleRemoveArtist(artistId)}
                          className="ml-2 hover:bg-red-600 rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="navy-card border-gray-600 text-gray-300"
            >
              {isFrozen ? "Close" : "Cancel"}
            </Button>
            {!isFrozen && (
              <Button
                onClick={handleSave}
                disabled={updateProjectMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {updateProjectMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Force Full Rebuild Confirmation Dialog (task #216) */}
      <AlertDialog open={showForceRebuildDialog} onOpenChange={setShowForceRebuildDialog}>
        <AlertDialogContent className="navy-card border-red-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Force full rebuild
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              This will wipe the cache. Large projects will time out on the first run and require multiple attempts to rebuild completely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="navy-card border-gray-600 text-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={(e) => {
                e.preventDefault();
                forceRebuildMutation.mutate();
              }}
              disabled={forceRebuildMutation.isPending}
              data-testid={`confirm-force-rebuild-${project.id}`}
            >
              {forceRebuildMutation.isPending ? 'Rebuilding…' : 'Yes, wipe & rebuild'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dropbox Backup Confirmation Dialog */}
      <AlertDialog open={showDropboxArchiveDialog} onOpenChange={setShowDropboxArchiveDialog}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-400" />
              Backup Project to Dropbox
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              <div className="space-y-3">
                <p>
                  You are about to create a backup of project <strong className="text-white">{project.projectName}</strong> ({project.id}).
                </p>
                <div className="bg-blue-500/10 border border-blue-500/50 rounded p-3 space-y-2">
                  <p className="text-blue-200 font-medium">This action will:</p>
                  <ul className="list-disc pl-5 text-blue-300/80 space-y-1 text-sm">
                    <li>Create a complete ZIP snapshot of all project data</li>
                    <li>Upload to Dropbox: /Project_Level_Backup/</li>
                    <li>Include all attachments and files</li>
                  </ul>
                </div>
                <p className="text-green-300 text-sm">
                  Non-destructive: Project data stays in the database. You can create multiple backups.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="navy-card border-gray-600 text-gray-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => backupToDropboxMutation.mutate()}
              disabled={backupToDropboxMutation.isPending}
            >
              {backupToDropboxMutation.isPending ? 'Backing up...' : 'Yes, Create Backup'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Project Confirmation (admin only, pulses=0) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="navy-card border-red-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete project {project.id}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-gray-300 space-y-3">
                <p>
                  You are about to permanently delete{' '}
                  <strong className="text-white">{project.projectName}</strong> ({project.id}).
                </p>
                <div className="bg-red-500/10 border border-red-500/50 rounded p-3 space-y-1">
                  <p className="text-red-200 font-medium">This cannot be undone.</p>
                  <p className="text-sm text-red-300/80">
                    The project record and all of its settings, artist assignments,
                    contacts, messages, and time-budget rows will be removed from the
                    database.
                  </p>
                </div>
                <p className="text-sm text-gray-400">
                  Available only because this project has no pulses yet.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="navy-card border-gray-600 text-gray-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={(e) => {
                e.preventDefault();
                deleteProjectMutation.mutate();
              }}
              disabled={deleteProjectMutation.isPending}
            >
              {deleteProjectMutation.isPending ? 'Deleting…' : 'Yes, delete project'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Warning Dialog - shows failed file details */}
      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent className="navy-card border-gray-600 max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-yellow-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Archive Completed with Warnings
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-gray-300 space-y-4 mt-4">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-400">
                    {warningDetails?.succeeded || 0} files downloaded successfully
                  </span>
                  <span className="text-red-400">
                    {warningDetails?.failed || 0} files failed
                  </span>
                </div>
                
                <div className="bg-gray-800 rounded p-3 max-h-80 overflow-y-auto">
                  <p className="text-gray-400 text-xs mb-2 font-medium">
                    Failed files (click to select, then copy):
                  </p>
                  <pre 
                    className="text-xs font-mono text-red-300 whitespace-pre-wrap select-all"
                    onClick={(e) => {
                      const selection = window.getSelection();
                      const range = document.createRange();
                      range.selectNodeContents(e.currentTarget);
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                    }}
                  >
                    {warningDetails?.failedFiles?.map((f, i) => 
                      `${i + 1}. ${f.path}\n   Error: ${f.error}\n`
                    ).join('\n')}
                  </pre>
                </div>
                
                <p className="text-sm text-gray-400">
                  These files could not be included in the backup archive. They may have been deleted from Dropbox 
                  or the file paths in the database are incorrect.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                setShowWarningDialog(false);
                setWarningDetails(null);
              }}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

/**
 * Read-only viewer for the LLM-generated search keywords stored on a
 * project (task #245). Renders the chip list, the last-updated time,
 * and the dirty counter so power users can see when the next sweep
 * will refresh them. No editing surface yet — that's a follow-up.
 */
interface ProjectSearchKeywordsSectionProps {
  projectId: string;
  open: boolean;
  isAdmin: boolean;
}

function ProjectSearchKeywordsSection({ projectId, open, isAdmin }: ProjectSearchKeywordsSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{
    searchKeywords: string[] | null;
    searchKeywordsUpdatedAt: string | null;
    searchKeywordsDirty: number;
  }>({
    queryKey: [`/api/projects/${projectId}`],
    enabled: open,
    staleTime: 30_000,
  });

  const regenerate = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', `/api/projects/${projectId}/search-keywords/refresh`);
    },
    onSuccess: async (res: any) => {
      const body = await (res?.json ? res.json().catch(() => null) : null);
      if (body && body.refreshed === false) {
        toast({
          title: 'Keywords not regenerated',
          description: body.reason || 'No change',
        });
      } else {
        toast({ title: 'Keywords regenerated' });
      }
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
    },
    onError: (err: any) => {
      toast({
        title: 'Regenerate failed',
        description: err?.message || 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const keywords = Array.isArray(data?.searchKeywords) ? data!.searchKeywords : [];
  const updatedAt = data?.searchKeywordsUpdatedAt
    ? new Date(data.searchKeywordsUpdatedAt)
    : null;
  const dirty = data?.searchKeywordsDirty ?? 0;

  return (
    <div className="space-y-3" data-testid="project-search-keywords-section">
      <div className="flex items-center justify-between border-b border-gray-600 pb-2 gap-2">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Tag className="w-4 h-4 text-blue-400" />
          Search Keywords
        </h3>
        {isAdmin && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs border-gray-600 text-gray-200 hover:bg-gray-700"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
            data-testid="project-search-keywords-regenerate"
            title="Run the LLM keyword extraction again now. Admin only."
          >
            {regenerate.isPending ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Regenerating…
              </>
            ) : (
              'Regenerate'
            )}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading keywords…
        </div>
      ) : keywords.length === 0 ? (
        <p className="text-sm text-gray-400">
          No keywords yet. They are generated automatically from the first,
          sixth, and twelfth public pulse on this project. After that,
          {isAdmin ? ' use Regenerate above' : ' an admin can regenerate them'} to refresh.
        </p>
      ) : (
        <>
          <div
            className="flex flex-wrap gap-1.5"
            data-testid="project-search-keywords-list"
          >
            {keywords.map((kw, i) => (
              <Badge
                key={`${kw}-${i}`}
                variant="secondary"
                className="bg-gray-700 text-gray-100 hover:bg-gray-700 font-normal"
                data-testid={`project-search-keyword-${i}`}
              >
                {kw}
              </Badge>
            ))}
          </div>
          <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              {keywords.length} keyword{keywords.length === 1 ? '' : 's'}
            </span>
            {updatedAt && (
              <span title={updatedAt.toISOString()}>
                Last refreshed {updatedAt.toLocaleString()}
              </span>
            )}
            {dirty >= 10 && (
              <span className="text-yellow-400/80" title="A refresh is queued for the next sweep tick (≤5 min)">
                refresh queued
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}